import { useCallback, useReducer, useState } from 'react';
import { addTeam, addUser, removeTeam, removeUser, setTeams, setUsers } from './CollabDiscovery.actions';
import reducer, { initialState } from './CollabDiscovery.reducer';

interface UseSearchableUserListProps {
    fetchMore: (
        options: { variables: { paginationInput: { offset: number; limit: number; direction: string; time: null } } },
    ) => Promise<{
        data: {
            getUsersByOrganization: {
                edges: Array<{
                    id: string;
                    email: string;
                    firstName: string;
                    lastName: string;
                    fullName: string;
                    profileImage: string;
                    organizationId: string;
                }>;
                pageInfo: { hasNextPage: boolean };
            };
        };
    }>;
}

export default function useSearchableUserList ({ fetchMore }: UseSearchableUserListProps) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const limit = 10;

    const handleAddUser = useCallback((user) => {
        dispatch(addUser(user));
    }, []);

    const handleRemoveUser = useCallback((id) => {
        dispatch(removeUser(id));
    }, []);

    const handleSetUsers = useCallback((users) => {
        dispatch(setUsers(users));
    }, []);

    const handleAddTeam = useCallback((team) => {
        dispatch(addTeam(team));
    }, []);

    const handleRemoveTeam = useCallback((id) => {
        dispatch(removeTeam(id));
    }, []);

    const handleSetTeams = useCallback((teams) => {
        dispatch(setTeams(teams));
    }, []);
    const resetState = useCallback(() => {
        setOffset(0);
        setHasMore(true);
        setIsLoadingMore(false);
        handleSetUsers({ userList: [] }); // Reset the user list
    }, [handleSetUsers]);

    const loadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore) return;

        setIsLoadingMore(true);
        const newOffset = offset + limit;

        try {
            const { data } = await fetchMore({
                variables: {
                    paginationInput: {
                        offset: newOffset,
                        limit,
                        direction: 'FORWARD',
                        time: null,
                    },
                },
            });

            if (data?.getUsersByOrganization) {
                const newUsers = data.getUsersByOrganization.edges;
                const hasNextPage = data.getUsersByOrganization.pageInfo.hasNextPage;

                setHasMore(hasNextPage);
                setOffset(newOffset);

                handleSetUsers({
                    userList: [...state.userList, ...newUsers],
                });
            }
        } catch (error) {
            console.error('Error loading more users:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, isLoadingMore, offset, handleSetUsers, state.userList, fetchMore]);

    return {
        users: state.userList,
        setUsers: handleSetUsers,
        addedUsers: state.addedUsers,
        addUser: handleAddUser,
        removeUser: handleRemoveUser,
        teams: state.teamList,
        setTeams: handleSetTeams,
        addedTeams: state.addedTeams,
        addTeam: handleAddTeam,
        removeTeam: handleRemoveTeam,
        hasMore,
        isLoadingMore,
        loadMore,
        resetState,
    };
}
